import {
	Button,
	TextField,
	Link,
	Stack,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
} from '@material-ui/core';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createUser } from '../../../api_helper/api';
import SnackbarAlertContext from '../../../context/SnackbarAlertContext';

const signUpSchema = z
	.object({
		firstName: z.string().min(1, 'First name is required'),
		lastName: z.string().min(1, 'Last name is required'),
		username: z.string().min(1, 'Username is required'),
		email: z.email('Please enter a valid email address'),
		password: z.string().min(6, 'Password must be at least 6 characters'),
		confirmPassword: z.string(),
		userHasAgreedToTermsOfService: z.boolean(),
		userHasReadPrivacyPolicy: z.boolean(),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords don't match",
		path: ['confirmPassword'],
	})
	.refine((data) => data.userHasReadPrivacyPolicy === true, {
		message: 'You must read the privacy policy',
		path: ['userHasReadPrivacyPolicy'],
	})
	.refine((data) => data.userHasAgreedToTermsOfService === true, {
		message: 'You must agree to the terms of service',
		path: ['userHasAgreedToTermsOfService'],
	});

const SignUpForm = ({ onSuccess }) => {
	const { register, formState, handleSubmit } = useForm({ resolver: zodResolver(signUpSchema), mode: 'all' });

	const snackbarAlertContext = useContext(SnackbarAlertContext);

	const [isLoading, setIsLoading] = useState(false);

	const formSubmitHandler = async (data) => {
		try {
			setIsLoading(true);

			const { firstName, lastName, username, email, password } = data;

			const res = await createUser({ firstName, lastName, username, email, password });

			onSuccess(res);
		} catch (error) {
			snackbarAlertContext.setSnackbarAlert({
				isSnackbarOpen: true,
				autoHide: 3000,
				msg: error.data || 'There was an error creating your account. Please try again later.',
				severity: 'error',
			});
		}

		setIsLoading(false);
	};

	return (
		<form onSubmit={handleSubmit(formSubmitHandler)}>
			<Stack sx={{ gap: 2 }}>
				<TextField
					{...register('firstName')}
					label="First Name"
					error={!!formState.errors.firstName}
					helperText={formState.errors.firstName?.message}
					disabled={isLoading}
				/>

				<TextField
					{...register('lastName')}
					label="Last Name"
					error={!!formState.errors.lastName}
					helperText={formState.errors.lastName?.message}
					disabled={isLoading}
				/>

				<TextField
					{...register('username')}
					label="Username"
					error={!!formState.errors.username}
					helperText={formState.errors.username?.message}
					disabled={isLoading}
				/>

				<TextField
					{...register('email')}
					label="Email"
					error={!!formState.errors.email}
					helperText={formState.errors.email?.message}
					disabled={isLoading}
				/>

				<TextField
					{...register('password')}
					label="Password"
					type="password"
					error={!!formState.errors.password}
					helperText={formState.errors.password?.message}
					disabled={isLoading}
				/>

				<TextField
					{...register('confirmPassword')}
					label="Confirm Password"
					type="password"
					error={!!formState.errors.confirmPassword}
					helperText={formState.errors.confirmPassword?.message}
					disabled={isLoading}
				/>

				<Stack>
					<FormControl error={!!formState.errors.userHasAgreedToTermsOfService}>
						<FormControlLabel
							control={<Checkbox {...register('userHasAgreedToTermsOfService')} />}
							label={
								<span>
									I agree to the{' '}
									<Link href="https://rolling-insights.com/terms-of-services/" target="_blank">
										Terms of Service.
									</Link>
								</span>
							}
							disabled={isLoading}
						/>
						<FormHelperText>{formState.errors.userHasAgreedToTermsOfService?.message}</FormHelperText>
					</FormControl>
					<FormControl error={!!formState.errors.userHasReadPrivacyPolicy}>
						<FormControlLabel
							control={<Checkbox {...register('userHasReadPrivacyPolicy')} />}
							label={
								<span>
									I have read the{' '}
									<Link href="https://rolling-insights.com/privacy-policy/" target="_blank">
										Privacy Policy.
									</Link>
								</span>
							}
							disabled={isLoading}
						/>
						<FormHelperText>{formState.errors.userHasReadPrivacyPolicy?.message}</FormHelperText>
					</FormControl>
				</Stack>
			</Stack>

			<Button variant="contained" color="primary" type="submit" fullWidth disabled={isLoading} sx={{ mt: 2 }}>
				Create Account & Add DataSpace
			</Button>
		</form>
	);
};

export default SignUpForm;
